'use client';

/**
 * BodyCompVelocityCard — replaces the simple "current weight + delta"
 * tiles with a rate-of-change view that separates fat vs lean mass
 * trajectory.
 *
 * Layout:
 *   - Phase badge (CUT / BULK / MAINTAIN) drives the headline
 *   - Three velocity tiles (lbs/week): Weight / Fat / Lean
 *   - Stacked-area chart showing fat vs lean mass over the window
 *   - Target-velocity comparison strip with a status pip
 *   - Plain-English coaching line (matches FormCurveCard pattern)
 *
 * Pure: caller passes the velocity summary. Null = render learning
 * placeholder. Wearable-less parity: requires a smart scale OR manual
 * weight + BF% logs over multiple weeks. There's no fallback that
 * works without both — without BF% there's no way to separate fat
 * from lean, and faking the split misleads the user.
 */

import { useMemo } from 'react';
import { TrendingUp, TrendingDown, Minus, Scale } from 'lucide-react';
import OpsPanel from './OpsPanel';
import type { BodyCompVelocitySummary, BodyCompPhase } from '@/lib/bodyCompositionVelocity';

interface Props {
  summary: BodyCompVelocitySummary | null;
}

const PHASE_LABEL: Record<BodyCompPhase, string> = {
  cut: 'CUT',
  bulk: 'BULK',
  maintain: 'MAINTAIN',
  unknown: 'DRIFTING',
};

const PHASE_TW: Record<BodyCompPhase, string> = {
  cut: 'text-amber-400',
  bulk: 'text-emerald-400',
  maintain: 'text-cyan-400',
  unknown: 'text-slate-400',
};

export default function BodyCompVelocityCard({ summary }: Props) {
  if (!summary) {
    return (
      <OpsPanel badge="Body Composition Velocity" code="SYS·BC·02" accent="cyan"
        callout={<span className="text-slate-500">LEARNING</span>}>
        <div className="flex items-start gap-2">
          <Scale className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
          <p className="text-[12px] text-slate-400 leading-snug">
            Need 14+ days of weight + body fat % data to track velocity. Manual log works — but consistency matters more than perfect accuracy. Pick a smart scale or weigh under the same conditions each morning.
          </p>
        </div>
      </OpsPanel>
    );
  }

  const phaseTw = PHASE_TW[summary.phase];

  return (
    <OpsPanel badge="Body Composition Velocity" code="SYS·BC·02" accent="cyan"
      callout={<span className={`${phaseTw} font-bold`}>{PHASE_LABEL[summary.phase]}</span>}>
      <div className="grid grid-cols-3 gap-2 mb-3">
        <VelocityTile
          label="Weight"
          value={summary.velocity.weightLbsPerWeek}
          unit="lbs/wk"
          desirable={summary.phase === 'cut' ? 'down' : summary.phase === 'bulk' ? 'up' : 'flat'}
        />
        <VelocityTile
          label="Fat"
          value={summary.velocity.fatLbsPerWeek}
          unit="lbs/wk"
          desirable="down"
        />
        <VelocityTile
          label="Lean"
          value={summary.velocity.leanLbsPerWeek}
          unit="lbs/wk"
          desirable="up"
        />
      </div>

      <BodyCompChartSvg points={summary.points} />

      {summary.targetWeightVelocity && (
        <TargetStrip
          actual={summary.velocity.weightLbsPerWeek}
          target={summary.targetWeightVelocity}
          phase={summary.phase}
        />
      )}

      <p className="text-[11px] text-slate-300 leading-snug mt-3">{summary.interpretation}</p>

      <div className="mt-2 pt-2 border-t border-slate-800/60 flex items-center justify-between font-mono text-[9px] text-slate-600 uppercase tracking-[0.18em] tabular-nums">
        <span>
          {summary.daysAnalyzed}d total · weight {summary.totalDelta.weightLbs > 0 ? '+' : ''}{summary.totalDelta.weightLbs.toFixed(1)}lb
        </span>
        <span>
          fat {summary.totalDelta.fatLbs > 0 ? '+' : ''}{summary.totalDelta.fatLbs.toFixed(1)} · lean {summary.totalDelta.leanLbs > 0 ? '+' : ''}{summary.totalDelta.leanLbs.toFixed(1)}
        </span>
      </div>
    </OpsPanel>
  );
}

function VelocityTile({ label, value, unit, desirable }: {
  label: string;
  value: number;
  unit: string;
  desirable: 'up' | 'down' | 'flat';
}) {
  // Color: green if moving in the desirable direction, amber otherwise,
  // slate when essentially flat (<0.1 lbs/wk).
  const abs = Math.abs(value);
  const flat = abs < 0.1;
  let valueTw: string;
  if (flat) {
    valueTw = desirable === 'flat' ? 'text-cyan-300' : 'text-slate-400';
  } else if (desirable === 'flat') {
    valueTw = abs > 0.5 ? 'text-amber-400' : 'text-slate-300';
  } else {
    const goodDirection =
      (desirable === 'up' && value > 0) ||
      (desirable === 'down' && value < 0);
    valueTw = goodDirection ? 'text-emerald-300' : 'text-amber-400';
  }

  const Icon = flat ? Minus : value > 0 ? TrendingUp : TrendingDown;
  const sign = value > 0 ? '+' : '';

  return (
    <div className="rounded-md border border-slate-800/80 bg-slate-900/40 px-2 py-2">
      <div className="flex items-center justify-between mb-1">
        <span className="font-mono text-[9px] text-slate-500 uppercase tracking-[0.18em]">{label}</span>
        <Icon className={`w-2.5 h-2.5 ${valueTw}`} />
      </div>
      <div className={`font-heading text-xl font-bold tabular-nums leading-none ${valueTw}`}>
        {sign}{value.toFixed(2)}
      </div>
      <div className="font-mono text-[8px] text-slate-600 uppercase tracking-[0.12em] mt-1">{unit}</div>
    </div>
  );
}

function TargetStrip({ actual, target, phase }: {
  actual: number;
  target: { lo: number; hi: number };
  phase: BodyCompPhase;
}) {
  const inBand = actual >= target.lo && actual <= target.hi;
  // Pip position — clamp to ±50% of the band width beyond either edge
  const band = target.hi - target.lo;
  const center = (target.lo + target.hi) / 2;
  const range = band === 0 ? 0.5 : band * 1.5;
  const pct = Math.max(0, Math.min(100, ((actual - (center - range / 2)) / range) * 100));
  const pipTw = inBand ? 'bg-emerald-400' : 'bg-amber-400';

  return (
    <div className="mt-2 rounded-md border border-slate-800/80 bg-slate-900/40 px-3 py-2">
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="font-mono text-[9px] text-slate-500 uppercase tracking-[0.18em]">
          Target band ({phase})
        </span>
        <span className="font-mono text-[10px] text-slate-300 uppercase tracking-[0.16em] tabular-nums">
          {target.lo.toFixed(2)} → {target.hi.toFixed(2)} lb/wk
        </span>
      </div>
      <div className="relative h-1.5 rounded-sm bg-slate-900 border border-slate-800 overflow-hidden">
        {/* Band background */}
        <div className="absolute inset-y-0 bg-emerald-500/15 border-x border-emerald-500/30" style={{ left: '33%', right: '33%' }} />
        {/* Pip */}
        <span
          className={`absolute top-1/2 -translate-y-1/2 w-2 h-2 rounded-full ${pipTw} shadow-[0_0_4px_currentColor] -translate-x-1/2`}
          style={{ left: `${pct}%` }}
          aria-label={`Actual ${actual.toFixed(2)} lbs/wk`}
        />
      </div>
      <div className={`font-mono text-[9px] uppercase tracking-[0.16em] mt-1 tabular-nums ${inBand ? 'text-emerald-400' : 'text-amber-400'}`}>
        Actual {actual > 0 ? '+' : ''}{actual.toFixed(2)} lb/wk · {inBand ? 'in band' : 'out of band'}
      </div>
    </div>
  );
}

function BodyCompChartSvg({ points }: { points: BodyCompVelocitySummary['points'] }) {
  const W = 320;
  const H = 100;
  const PAD_L = 28;
  const PAD_R = 8;
  const PAD_T = 8;
  const PAD_B = 14;
  const plotW = W - PAD_L - PAD_R;
  const plotH = H - PAD_T - PAD_B;

  const { yMin, yMax } = useMemo(() => {
    let yMin = Infinity, yMax = -Infinity;
    for (const p of points) {
      if (p.weightLbs < yMin) yMin = p.weightLbs;
      if (p.weightLbs > yMax) yMax = p.weightLbs;
    }
    const pad = Math.max(2, (yMax - yMin) * 0.15);
    return { yMin: yMin - pad, yMax: yMax + pad };
  }, [points]);

  const xAt = (i: number) => PAD_L + (i / Math.max(1, points.length - 1)) * plotW;
  const yAt = (v: number) => PAD_T + (1 - (v - yMin) / (yMax - yMin)) * plotH;

  // Three lines: total weight, lean mass, fat mass (offset visually so
  // they don't all share one y-scale at the bottom)
  const weightPath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i).toFixed(1)} ${yAt(p.weightLbs).toFixed(1)}`).join(' ');
  const leanPath   = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i).toFixed(1)} ${yAt(p.leanMassLbs).toFixed(1)}`).join(' ');
  const fatPath    = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i).toFixed(1)} ${yAt(p.fatMassLbs).toFixed(1)}`).join(' ');

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
        {/* Y-axis labels (top + bottom of range) */}
        <text x="2" y={yAt(yMax) + 4} className="fill-slate-600 font-mono" fontSize="7">{yMax.toFixed(0)}</text>
        <text x="2" y={yAt(yMin) + 4} className="fill-slate-600 font-mono" fontSize="7">{yMin.toFixed(0)}</text>

        <path d={weightPath} className="stroke-cyan-300 fill-none" strokeWidth="1.2" />
        <path d={leanPath} className="stroke-emerald-400 fill-none" strokeWidth="1" strokeDasharray="3 2" />
        <path d={fatPath} className="stroke-amber-400 fill-none" strokeWidth="1" strokeDasharray="3 2" />

        {/* End markers */}
        {points.length > 0 && (
          <>
            <circle cx={xAt(points.length - 1)} cy={yAt(points[points.length - 1].weightLbs)} r="2" className="fill-cyan-300" />
          </>
        )}

        <text x={PAD_L} y={H - 2} className="fill-slate-600 font-mono" fontSize="7" textAnchor="start">
          {formatChartDate(points[0]?.date)}
        </text>
        <text x={W - PAD_R} y={H - 2} className="fill-slate-600 font-mono" fontSize="7" textAnchor="end">
          today
        </text>
      </svg>
      <div className="flex items-center justify-center gap-4 mt-1 font-mono text-[9px] uppercase tracking-[0.14em]">
        <LegendDot tw="bg-cyan-300" label="Weight" />
        <LegendDot tw="bg-emerald-400" label="Lean" dashed />
        <LegendDot tw="bg-amber-400" label="Fat" dashed />
      </div>
    </div>
  );
}

function LegendDot({ tw, label, dashed }: { tw: string; label: string; dashed?: boolean }) {
  return (
    <span className="flex items-center gap-1.5 text-slate-500">
      <span className={`w-3 h-px ${tw} ${dashed ? 'opacity-70' : ''}`} aria-hidden />
      <span>{label}</span>
    </span>
  );
}

function formatChartDate(iso?: string): string {
  if (!iso) return '';
  const [, m, d] = iso.split('-').map(Number);
  if (!m || !d) return '';
  return `${m}/${d}`;
}
